import axios from 'axios';

const SMM_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:6555');

const api = axios.create({ baseURL: SMM_URL, timeout: 15000 });
const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map();
window.addEventListener('portal-access-changed', () => cache.clear());

const cachedGet = (key, request) => {
  key = `${getSmmAuthToken()}:${key}`;
  const cached = cache.get(key);
  if (cached && Date.now() - cached.time < CACHE_TTL_MS) {
    return Promise.resolve(cached.data);
  }
  if (cached?.promise) return cached.promise;

  const promise = request()
    .then((data) => {
      cache.set(key, { data, time: Date.now() });
      return data;
    })
    .catch((error) => {
      cache.delete(key);
      throw error;
    });

  cache.set(key, { promise, time: Date.now() });
  return promise;
};

const isAdminPath = () => window.location.pathname.startsWith('/admin');

const redirectExpiredAdminSession = () => {
  if (!isAdminPath()) return false;
  if (window.location.pathname === '/admin/login') return true;
  localStorage.removeItem('admin_token');
  localStorage.removeItem('portal_token');
  window.location.replace('/admin/login');
  return true;
};

const getSmmAuthToken = () => {
  if (isAdminPath()) {
    return localStorage.getItem('admin_token') || localStorage.getItem('portal_token');
  }
  return localStorage.getItem('smm_token') || localStorage.getItem('admin_token') || localStorage.getItem('portal_token');
};

api.interceptors.request.use((config) => {
  const token = getSmmAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      if (redirectExpiredAdminSession()) {
        return Promise.reject(err);
      }
      // Standalone SMM sessions use their own token and login screen.
      if (!isAdminPath()) {
        localStorage.removeItem('smm_token');
        window.location.href = '/smm/login';
      }
    }
    return Promise.reject(err);
  },
);

// Auth
export const smmLoginAdmin = (password) =>
  api.post('/auth/admin/login', { password }).then(r => r.data);
export const smmLoginClient = (password) =>
  api.post('/auth/client/login', { password }).then(r => r.data);

// Platforms
export const getPlatforms = () => api.get('/platforms/').then(r => r.data);
export const getPlatform = (slug) => api.get(`/platforms/${slug}`).then(r => r.data);
export const updatePlatform = (slug, data) =>
  api.patch(`/platforms/${slug}`, data).then(r => r.data);

// Categories
export const getCategories = () => cachedGet('categories', () => api.get('/categories/').then(r => r.data));
export const createCategory = (data) =>
  api.post('/categories/', data).then(r => { cache.delete('categories'); return r.data });
export const updateCategory = (id, data) =>
  api.patch(`/categories/${id}`, data).then(r => { cache.delete('categories'); return r.data });
export const deleteCategory = (id) =>
  api.delete(`/categories/${id}`).then(r => { cache.delete('categories'); return r.data });

// Posts
export const getPosts = (params) =>
  api.get('/posts/', { params }).then(r => r.data);
export const updatePost = (id, data) =>
  api.patch(`/posts/${id}`, data).then(r => r.data);

// Monthly stats
export const getMonthlyStats = (platformSlug) =>
  api.get(`/monthly-stats/${platformSlug}`).then(r => r.data);
export const createMonthlyStats = (data) =>
  api.post('/monthly-stats/', data).then(r => r.data);
export const updateMonthlyStats = (id, data) =>
  api.put(`/monthly-stats/${id}`, data).then(r => r.data);
export const deleteMonthlyStats = (id) =>
  api.delete(`/monthly-stats/${id}`).then(r => r.data);

// Sync
export const syncVK = (groupId) =>
  api.post('/sync/vk', { group_id: groupId }).then(r => r.data);
export const vkDiagnose = () => api.get('/vk/diagnose').then(r => r.data);
export const getSyncStatus = () => api.get('/sync/status').then(r => r.data);

// VK OAuth
export const getVKOAuthUrl = () => api.get('/vk-oauth/url').then(r => r.data);
export const exchangeVKCode = (code) =>
  api.post('/vk-oauth/exchange', { code }).then(r => r.data);

// Dashboard
export const getDashboardKPI = (params) =>
  api.get('/dashboard/kpi', { params }).then(r => r.data);
export const getDashboardOverview = (params) =>
  api.get('/dashboard/overview', { params }).then(r => r.data);
export const getTopPosts = (limit = 5) =>
  api.get('/dashboard/top-posts', { params: { limit } }).then(r => r.data);
export const getMoM = () => api.get('/dashboard/mom').then(r => r.data);

// VK analytics
export const getVKKpi = (params) =>
  api.get('/vk/kpi', { params }).then(r => r.data);
export const getVKDailyStats = (params) =>
  api.get('/vk/daily-stats', { params }).then(r => r.data);
export const getVKSubscribersHistory = (params) =>
  api.get('/vk/subscribers-history', { params }).then(r => r.data);
export const getVKSubscribersDaily = (params) =>
  api.get('/vk/subscribers-daily', { params }).then(r => r.data);
export const getVKPostEr = (limit = 10) =>
  api.get('/vk/post-er', { params: { limit } }).then(r => r.data);
export const getVKMoM = (monthsCount = 3) =>
  api.get('/vk/mom', { params: { months_count: monthsCount } }).then(r => r.data);

// Telegram analytics
export const getTGKpi = (params) =>
  api.get('/telegram/kpi', { params }).then(r => r.data);
export const getTGChartData = (days = 30) =>
  api.get('/telegram/chart-data', { params: { days } }).then(r => r.data);
export const getTGMoM = (monthsCount = 6) =>
  api.get('/telegram/mom', { params: { months_count: monthsCount } }).then(r => r.data);
export const getTGPosts = (params) =>
  api.get('/telegram/posts', { params }).then(r => r.data);
export const getTGPostsByCategory = (params) =>
  api.get('/telegram/posts-by-category', { params }).then(r => r.data);
export const syncTG = () => api.post('/telegram/sync').then(r => r.data);

// Instagram analytics (Meta Graph API)
const igCacheKey = (resource, params = {}) =>
  `ig:${resource}:${JSON.stringify(params, Object.keys(params).sort())}`;

export const invalidateIGCache = () => {
  for (const key of cache.keys()) {
    if (key.includes(':ig:')) cache.delete(key);
  }
};

export const getIGStatus = ({ fresh = false } = {}) => {
  const request = () => api.get('/instagram/status').then(r => r.data);
  return fresh ? request() : cachedGet(igCacheKey('status'), request);
};
export const getIGOverview = (days = 30) =>
  cachedGet(igCacheKey('overview', { days }), () =>
    api.get('/instagram/overview', { params: { days } }).then(r => r.data));
export const getIGDaily = (days = 30) =>
  cachedGet(igCacheKey('daily', { days }), () =>
    api.get('/instagram/daily', { params: { days } }).then(r => r.data));
export const getIGPosts = (params = {}) =>
  cachedGet(igCacheKey('posts', params), () =>
    api.get('/instagram/posts', { params }).then(r => r.data));
export const getIGMonthly = (monthsCount = 6) =>
  cachedGet(igCacheKey('monthly', { months_count: monthsCount }), () =>
    api.get('/instagram/monthly', { params: { months_count: monthsCount } }).then(r => r.data));
export const syncIG = () =>
  api.post('/instagram/sync', null, { timeout: 600000 }).then((r) => {
    invalidateIGCache();
    return r.data;
  });

// Compatibility aliases for older callers. New screens use the endpoints above.
export const getIGKpi = (params) => getIGOverview(params?.days || 30);
export const getIGChartData = (days = 30) => getIGDaily(days);
export const getIGMoM = (monthsCount = 6) => getIGMonthly(monthsCount);

export default api;
